/* eslint-disable react/no-unstable-nested-components */
import type { InvoiceLineItem } from 'payments-api/model';
import type { InvoiceStatus } from 'payments-api/model/invoiceStatus';
import {
  useGetInvoicesSuspense,
  useGetProductsSuspense,
  useGetUsersSuspense,
  usePostInvoicesId,
} from 'api/payments/generated/fragmentPaymentsAPI';
import { MainViewLayout } from 'components/Layout/MainViewLayout';
import { useToast } from 'components/ToastManager';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import {
  AddLineItemOperationOp,
  DeleteLineItemOperationOp,
  UpdateLineItemOperationOp,
} from 'payments-api/model';
import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { makePath } from 'utils/routes';
import { useLocation } from 'wouter';
import { useQueryClient } from '@tanstack/react-query';

import lilId from '@fragment/lil-id';
import { Toast } from '@fragment/ui/src/components/Toast';

import type { ItemTableItem } from '../ItemTable';
import { InvoiceItemsSection } from '../InvoiceItemsSection';
import { InvoiceLayout } from '../InvoiceLayout';

type PaymentRecord = {
  amount: number;
  datePaid: string;
};

type Props = {
  workspaceId: string;
  invoiceId: string;
};

const lineItemsToItemTableItems = (
  lineItems: InvoiceLineItem[] | undefined,
  paymentRecord?: Map<string, PaymentRecord>
): ItemTableItem[] => {
  if (!lineItems) return [];
  return lineItems.map((li) => ({
    id: li.id,
    type: li.type,
    product_id: li.product_id,
    amount: li.amount,
    currencyCode: li.currencyCode,
    description: li.description,
    user_id: li.user_id,
    paid: paymentRecord?.get(li.id)?.amount || 0,
  }));
};

const InvoiceDetailPageContent = ({
  workspaceId,
  invoiceId,
}: Props): JSX.Element => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { data } = useGetInvoicesSuspense();
  const { data: productsData } = useGetProductsSuspense();
  const { data: usersData } = useGetUsersSuspense();
  const postInvoicesIdMutation = usePostInvoicesId();
  const [, setLocation] = useLocation();
  const [isAdjusting, setIsAdjusting] = useState(false);
  const [invoiceItems, setInvoiceItems] = useState<ItemTableItem[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('all');

  const usersDataList = usersData.data;
  const products = productsData.data;

  const invoice = useMemo(
    () => data.data.find((inv) => inv.id === invoiceId),
    [data, invoiceId]
  );

  useDocumentTitle(invoice ? `Invoice ${invoice.id}` : 'Invoice Detail');

  // Fill this in with data once we have balances api
  const paymentRecord: Map<string, PaymentRecord> = useMemo(
    () =>
      new Map(
        invoice?.lineItems?.map((li) => [li.id, { amount: 0, datePaid: '' }]) ||
          []
      ),
    [invoice?.lineItems]
  );

  const displayItems = isAdjusting
    ? invoiceItems
    : lineItemsToItemTableItems(invoice?.lineItems, paymentRecord);

  const users = useMemo(
    () => [
      ...new Set(displayItems.map((item) => item.user_id).filter(Boolean)),
    ],
    [displayItems]
  );

  const handleBackToInvoices = useCallback(() => {
    setLocation(
      makePath(['w', workspaceId, 'payments', 'invoices'], { absolute: true })
    );
  }, [workspaceId, setLocation]);

  const handleStartAdjust = useCallback(() => {
    setInvoiceItems(lineItemsToItemTableItems(invoice?.lineItems));
    setIsAdjusting(true);
  }, [invoice?.lineItems]);

  const handleAddInvoiceItem = useCallback(
    (item: Omit<ItemTableItem, 'id'>) => {
      setInvoiceItems((prev) => [...prev, { ...item, id: lilId() }]);
    },
    []
  );

  const handleUpdateInvoiceItem = useCallback(
    (id: string, item: Omit<ItemTableItem, 'id'>) => {
      setInvoiceItems((prev) =>
        prev.map((i) => (i.id === id ? { ...item, id } : i))
      );
    },
    []
  );

  const removeItem = useCallback((id: string) => {
    setInvoiceItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const handleSubmitChanges = useCallback(async () => {
    if (!invoiceId || !invoice?.lineItems) return;
    const serverIds = new Set(invoice.lineItems.map((li) => li.id));
    const serverMap = new Map(invoice.lineItems.map((li) => [li.id, li]));

    const removes = invoice.lineItems
      .filter((li) => !invoiceItems.some((i) => i.id === li.id))
      .map((li) => ({
        op: DeleteLineItemOperationOp.delete,
        id: li.id,
      }));

    const updates = invoiceItems
      .filter((i) => serverIds.has(i.id))
      .filter((i) => {
        const server = serverMap.get(i.id);
        return (
          server &&
          (String(server.amount) !== String(i.amount) ||
            server.description !== i.description ||
            server.product_id !== i.product_id ||
            server.user_id !== i.user_id ||
            server.currencyCode !== i.currencyCode ||
            server.type !== i.type)
        );
      })
      .map((i) => ({
        op: UpdateLineItemOperationOp.update,
        id: i.id,
        amount: i.amount,
        description: i.description,
        product_id: i.product_id,
        user_id: i.user_id,
        currency_code: i.currencyCode,
        type: i.type,
      }));

    const adds = invoiceItems
      .filter((i) => !serverIds.has(i.id))
      .map((i) => ({
        op: AddLineItemOperationOp.add,
        type: i.type,
        product_id: i.product_id,
        amount: i.amount,
        currency_code: i.currencyCode,
        description: i.description,
        user_id: i.user_id,
      }));

    const lineItemsUpdate = [...removes, ...updates, ...adds];
    if (lineItemsUpdate.length === 0) return;

    try {
      await postInvoicesIdMutation.mutateAsync({
        id: invoiceId,
        data: {
          // Gets around a bug in the API
          version: Math.max(invoice!.version, 1),
          line_items_update: lineItemsUpdate,
        },
      });
      await queryClient.invalidateQueries({ queryKey: ['/invoices'] });
      setIsAdjusting(false);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to save changes.';
      showToast(<Toast type="error" message={message} />);
    }
  }, [
    invoiceId,
    invoice,
    invoiceItems,
    postInvoicesIdMutation,
    queryClient,
    showToast,
  ]);

  const handleCancelAdjust = useCallback(() => {
    setIsAdjusting(false);
  }, []);

  const hasChanges = useMemo(() => {
    if (!invoice?.lineItems || !isAdjusting) return false;
    const serverIds = new Set(invoice.lineItems.map((li) => li.id));
    const serverMap = new Map(invoice.lineItems.map((li) => [li.id, li]));
    const currentIds = new Set(invoiceItems.map((i) => i.id));
    if (invoice.lineItems.some((li) => !currentIds.has(li.id))) return true;
    if (invoiceItems.some((i) => !serverIds.has(i.id))) return true;
    return invoiceItems.some((i) => {
      const server = serverMap.get(i.id);
      return (
        server &&
        (String(server.amount) !== String(i.amount) ||
          server.description !== i.description ||
          server.product_id !== i.product_id ||
          server.user_id !== i.user_id ||
          server.currencyCode !== i.currencyCode ||
          server.type !== i.type)
      );
    });
  }, [invoice?.lineItems, isAdjusting, invoiceItems]);

  const canSubmitAdjustment = hasChanges && !postInvoicesIdMutation.isPending;

  const primaryActionTitle = useMemo(() => {
    if (postInvoicesIdMutation.isPending) return 'Wait...';
    if (isAdjusting) return 'Submit';
    return 'Adjust';
  }, [postInvoicesIdMutation.isPending, isAdjusting]);

  if (!invoice) {
    return (
      <MainViewLayout hideSearch>
        <div className="text-main-500">Invoice not found</div>
      </MainViewLayout>
    );
  }

  return (
    <>
      <InvoiceLayout
        onBack={handleBackToInvoices}
        title={invoice.id}
        status={invoice.status as InvoiceStatus}
        primaryAction={{
          title: primaryActionTitle,
          'data-testid': 'submit-invoice-button',
          onClick: isAdjusting ? handleSubmitChanges : handleStartAdjust,
          disabled: isAdjusting ? !canSubmitAdjustment : false,
          disabledTooltipText: isAdjusting ? undefined : undefined,
        }}
        secondaryAction={
          isAdjusting
            ? {
                title: 'Cancel',
                'data-testid': 'cancel-invoice-button',
                onClick: handleCancelAdjust,
              }
            : {
                title: 'History',
                'data-testid': 'history-invoice-button',
                onClick: () => {},
              }
        }
        issued={invoice.created}
        users={users}
        selectedUserId={selectedUserId}
        setSelectedUserId={setSelectedUserId}
      >
        <InvoiceItemsSection
          items={displayItems}
          products={products}
          users={usersDataList}
          selectedUserId={selectedUserId}
          onAddItem={isAdjusting ? handleAddInvoiceItem : undefined}
          onUpdateItem={isAdjusting ? handleUpdateInvoiceItem : undefined}
          onRemoveItem={isAdjusting ? removeItem : undefined}
          showUnbalancedWarning={isAdjusting}
        />
      </InvoiceLayout>
    </>
  );
};

export const InvoiceDetailPage = ({
  workspaceId,
  invoiceId,
}: Props): JSX.Element => (
  <Suspense
    fallback={
      <MainViewLayout hideSearch>
        <div className="text-main-500">Loading invoice...</div>
      </MainViewLayout>
    }
  >
    <InvoiceDetailPageContent workspaceId={workspaceId} invoiceId={invoiceId} />
  </Suspense>
);
